import { useDispatch } from "ko-data-store";
import manageReportsService, {
  IBiReport,
  IBiReportCategory,
  IBiReportTypeOption,
  IRoleOption,
  ITierOption,
} from "./manageReportsService";
import { isLoading } from "../../../dataStore/actions/appUI";
import toastNotify from "geNotyf";
import { showconfirm } from "show-dialog-methods";

class BiReportTypeOption {
  typeTerm: ko.Observable<string> = ko.observable("link");
  url: ko.Observable<string> = ko.observable("");
  biEmbedId: ko.Observable<string> = ko.observable("");
  biWorkspaceId: ko.Observable<string> = ko.observable("");

  constructor(x?: any) {
    x = x ?? {};
    this.typeTerm(x.typeTerm ?? "link");
    this.biWorkspaceId(x.biWorkspaceId);
    this.biEmbedId(x.biEmbedId);
    this.url(x.url);
  }
}

class BiReportModel {
  id: number = -1;
  title: ko.Observable<string> = ko.observable("");
  description: ko.Observable<string> = ko.observable("");
  category: ko.Observable<IBiReportCategory> = ko.observable();
  tier: ko.Observable<ITierOption | null> = ko.observable();
  isActive: ko.Observable<boolean> = ko.observable(true);

  biReportType: ko.Observable<BiReportTypeOption> = ko.observable(
    new BiReportTypeOption()
  );

  constructor(x?: any) {
    x = x ?? {};
    this.id = -1;

    if (x) {
      this.id = x.id;
      this.title(x.title);
      this.description(x.description);
      this.category(x.category);
      this.tier(x.tier);
      this.isActive(x.isActive ?? true);

      this.biReportType(new BiReportTypeOption(x.biReportType));
    }
  }
}

class ManageReportsFormViewModel {
  dispatch = useDispatch();
  reportTypeOption: ko.Observable<string> = ko.observable("link");
  reportOptions: ko.ObservableArray<BiReportModel> = ko.observableArray([]);
  selectedReport: ko.Observable<BiReportModel | null> = ko.observable(null);
  editReportModel: ko.Observable<BiReportModel | null> = ko.observable(null);
  selectedCategory: ko.Observable<IBiReportCategory | null> =
    ko.observable(null);
  selectedTier: ko.Observable<ITierOption | null> = ko.observable(null);

  tierOptions: ko.ObservableArray<ITierOption> = ko.observableArray([]);
  categoryOptions: ko.ObservableArray<IBiReportCategory> = ko.observableArray(
    []
  );
  tierAssignedAgencies: ko.ObservableArray<string> = ko.observableArray([]);

  availableRoles: ko.ObservableArray<IRoleOption> = ko.observableArray([]);
  assignedRoles: ko.ObservableArray<IRoleOption> = ko.observableArray([]);
  roleAssignColumns = [
    { text: "id", datafield: "id", hidden: true, columnType: "number" },
    { text: "Name", datafield: "name", columnType: "textbox" },
  ];
  roleAssignDataFields = [
    { name: "id", type: "string" },
    { name: "name", type: "string" },
  ];

  constructor() {
    this.loadCategoryOptions()
      .then(() => this.loadTierOptions())
      .then(() => this.loadReports())
      .then(async () => {
        this.editReportModel(new BiReportModel({ id: -1 } as IBiReport));
        await this.handleLoadAssignedRoles(-1);
      });

    this.selectedReport.subscribe((newReport) => {
      this.handleSelectedReport(newReport);
    });

    this.selectedTier.subscribe(async (tier) => {
      if (tier) {
        await this.handleLoadAssignedAgencies(tier.tierId);
      } else {
        this.tierAssignedAgencies([]); // Clear assigned agencies if no tier selected
      }

      this.editReportModel().tier(tier);
    });

    this.selectedCategory.subscribe((category) => {
      if (!category) return;

      this.editReportModel().category(category);
    });

    this.reportTypeOption.subscribe((val) => {
      this.editReportModel().biReportType().typeTerm(val);
    });
  }

  loadReports = async () => {
    try {
      this.dispatch(isLoading(true));
      const result = await manageReportsService.fetchReportOptions();

      if (!result) return;

      const mapped = result.map((r) => new BiReportModel(r));
      this.reportOptions(mapped);
    } catch (error) {
      console.error("Error loading reports:", error);
    } finally {
      this.dispatch(isLoading(false));
    }
  };

  loadTierOptions = async () => {
    try {
      this.dispatch(isLoading(true));
      const result = await manageReportsService.fetchTierOptions();
      if (result) {
        this.tierOptions(result);
      }
    } catch (error) {
      console.error("Error loading tier options:", error);
    } finally {
      this.dispatch(isLoading(false));
    }
  };

  loadCategoryOptions = async () => {
    try {
      this.dispatch(isLoading(true));
      const result = await manageReportsService.fetchCategoryOptions();
      if (result) {
        this.categoryOptions(result);
      }
    } catch (error) {
      console.error("Error loading category options:", error);
    } finally {
      this.dispatch(isLoading(false));
    }
  };

  handleSelectedReport = async (reportKo: BiReportModel | null) => {
    this.tierAssignedAgencies([]);
    this.assignedRoles([]);
    this.availableRoles([]);

    if (reportKo == null) {
      this.editReportModel(new BiReportModel({ id: -1 } as IBiReport));
      this.selectedCategory(null);
      this.selectedTier(null);
      this.reportTypeOption("link");
      await this.handleLoadAssignedRoles(-1);
      return;
    }

    const report = ko.toJS(reportKo);
    this.editReportModel(new BiReportModel(report));

    // have to use the options provided as knockout stores reference when using objects.
    const categoryRef = this.categoryOptions()?.find(
      (x) => x.id == report.category.id
    );
    this.selectedCategory(categoryRef);

    if (report.tier && report.tier) {
      const tierRef = this.tierOptions().find(
        (x) => x.tierId == report.tier.tierId
      );
      this.selectedTier(tierRef || null);
    }

    this.reportTypeOption((report.biReportType.typeTerm ?? "link") as string);

    await this.handleLoadAssignedRoles(report.id);
  };

  computeDisplayReportOptionText = (option: BiReportModel) => {
    if (option.tier()) {
      return `${option.title()} (Tier ${option.tier()!.tierName})`;
    }

    if (option.description()) {
      return `${option.title()} - ${option.description().substring(0, 30)}...`;
    }

    return option.title();
  };

  handleSaveReport = async () => {
    try {
      this.dispatch(isLoading(true));
      const report = this.editReportModel();
      if (!report) {
        toastNotify.error("No report selected to save.");
        return;
      }

      if (
        !report.title() ||
        (report.biReportType().typeTerm() == "embed"
          ? !!report.biReportType().biWorkspaceId() &&
            !report.biReportType().biEmbedId()
          : !report.biReportType().url()) ||
        !report.category()
      ) {
        toastNotify.error("Please fill in all required fields.");
        return;
      }

      if (this.reportTypeOption() == "embed") {
        if (
          !report.biReportType().biEmbedId() ||
          !report.biReportType().biWorkspaceId()
        ) {
          toastNotify.error("Embed Id and Workspace Id is required.");
          return;
        }
      } else if (this.reportTypeOption() == "link") {
        if (!report.biReportType().url()) {
          toastNotify.error("URL is required.");
          return;
        }
      }

      const payload = ko.toJS(this.editReportModel());
      await manageReportsService.upsertReport({
        ...payload,
        assignedRoles: this.assignedRoles(),
      });

      toastNotify.success("Report saved successfully.");
      await this.resetForm();
    } catch (error) {
      console.error("Error saving report:", error);
      if (error instanceof Error) {
        toastNotify.open({
          message: error.message,
          duration: 5000,
          type: "error",
        });
      }

      if (typeof error === "string") {
        toastNotify.open({ message: error, duration: 5000, type: "error" });
      }
    } finally {
      this.dispatch(isLoading(false));
    }
  };

  handleLoadAssignedAgencies = async (tierId: number) => {
    try {
      this.dispatch(isLoading(true));
      const result = await manageReportsService.fetchAssignmentOptions(tierId);
      if (result && result.assignedAgencies) {
        this.tierAssignedAgencies(result.assignedAgencies.map((a) => a.name));
        this.buildAgencyTierGrid();
        return;
      }

      this.tierAssignedAgencies([]);
    } catch (error) {
      console.error("Error loading assigned agencies:", error);
    } finally {
      this.dispatch(isLoading(false));
    }
  };

  handleLoadAssignedRoles = async (reportId: number) => {
    try {
      this.dispatch(isLoading(true));
      const result = await manageReportsService.fetchRoleAssignmentsByReport(
        reportId
      );
      if (result) {
        this.assignedRoles(result.assignedRoles);
        this.availableRoles(result.availableRoles);
      }
    } catch (error) {
      console.error("Error loading assigned roles:", error);
    } finally {
      this.dispatch(isLoading(false));
    }
  };

  handleAddNewReport = () => {
    this.selectedReport(null);
    this.editReportModel(new BiReportModel({ id: -1 } as IBiReport));
  };

  resetForm = () => {
    this.selectedReport(null);
    this.editReportModel(new BiReportModel({ id: -1 } as IBiReport));
    this.tierAssignedAgencies([]);
    this.assignedRoles([]);
    this.availableRoles([]);
    this.selectedCategory(null);
    this.selectedTier(null);

    this.loadReports();
  };

  handleDeleteReport = async () => {
    try {
      const report = this.editReportModel();
      if (!report || report.id === -1) {
        toastNotify.error("No report selected to delete.");
        return;
      }

      if (
        !(await showconfirm(
          `Are you sure you want to delete the report "${report.title()}"? This action cannot be undone.`
        ))
      ) {
        return;
      }

      this.dispatch(isLoading(true));
      await manageReportsService.deleteReport(report.id);
      toastNotify.success("Report deleted successfully.");
      await this.resetForm();
    } catch (error) {
      console.error("Error deleting report:", error);
      if (error instanceof Error) {
        toastNotify.error(error.message);
      }

      if (typeof error === "string") {
        toastNotify.error(error);
      }
    } finally {
      this.dispatch(isLoading(false));
    }
  };

  buildAgencyTierGrid = () => {
    if (!$("#jqxAssignedTierAgenciesGrid").length) {
      return;
    }

    const source = {
      localdata: this.tierAssignedAgencies().map((x) => ({ agency: x })),
      datatype: "array",
      datafields: [{ name: "agency", type: "string" }],
    };

    const dataAdapter = new $.jqx.dataAdapter(source);

    $("#jqxAssignedTierAgenciesGrid").jqxGrid({
      height: "200px",
      source: dataAdapter,
      filterable: true,
      sortable: true,
      columns: [{ text: "Agency", datafield: "agency" }],
      altrows: true,
      showfilterrow: true,
      enablebrowserselection: true,
      columnsmenu: false,
      autoshowfiltericon: true,
    });
  };
}

export default ManageReportsFormViewModel;
